import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import { Calendar as BigCalendar, dateFnsLocalizer, Views, type View, type EventProps, type SlotInfo } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Calendar as CalendarIcon, Clock, ChevronLeft, ChevronRight, ClipboardList, Plus, Route as RouteIcon } from 'lucide-react';
import { format, parse, startOfWeek, startOfDay, getDay, addDays, addMonths } from 'date-fns';
import { es } from 'date-fns/locale';
import clsx from 'clsx';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/Button';
import { TripDetailModal } from '../../components/trips/TripDetailModal';
import { TripFormModal } from '../../components/trips/TripFormModal';
import { LogbookEntryDetailModal } from '../../components/logbook/LogbookEntryDetailModal';
import { useTrips } from '../../hooks/useTrips';
import { useLogbookEntries } from '../../hooks/useLogbookEntries';
import { useVehicles } from '../../hooks/useVehicles';
import { useDrivers } from '../../hooks/useDrivers';
import { puedeProgramar } from '../../utils/trips';
import {
  applyFilters, logbookToItem, tripToItem, visibleRange,
  type CalendarFilters, type CalendarItem, type CalendarKind, type CalendarViewName,
} from '../../utils/calendarEvents';

// El calendario vive de datos reales: los viajes programados (`/trips`) y las
// novedades de la bitácora (`/logbook-entries`) del rango que se está viendo.
// No existe un contrato de calendario ni hace falta — ver `utils/calendarEvents.ts`.

interface RBCEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  resource: CalendarItem;
}

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }),
  getDay,
  locales: { es },
});

const VIEW_OPTIONS: { key: View; label: string }[] = [
  { key: Views.MONTH, label: 'Mes' },
  { key: Views.WEEK, label: 'Semana' },
  { key: Views.DAY, label: 'Día' },
];

// El eje cubre las 24 h a propósito: un viaje o una novedad de madrugada
// existe igual, y un eje recortado (antes 06:00–22:00, pensado para el mock) los
// dejaría invisibles en Semana y Día. El scroll arranca a las 06:00 para que la
// jornada normal siga siendo lo primero que se ve.
const AXIS_MIN = new Date(1970, 0, 1, 0, 0);
const AXIS_MAX = new Date(1970, 0, 1, 23, 59);
const AXIS_SCROLL_TO = new Date(1970, 0, 1, 6, 0);

const KIND_OPTIONS: { value: CalendarKind | 'all'; label: string }[] = [
  { value: 'all', label: 'Todos los eventos' },
  { value: 'trip', label: '🛣️ Viajes' },
  { value: 'logbook', label: '📝 Novedades' },
];

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function timeLabel(item: CalendarItem): string {
  return item.kind === 'logbook'
    ? format(item.start, 'HH:mm')
    : `${format(item.start, 'HH:mm')} – ${format(item.end, 'HH:mm')}`;
}

function CalendarEventContent({ event, view }: EventProps<RBCEvent> & { view: View }) {
  const item = event.resource;
  const Icon = item.kind === 'trip' ? RouteIcon : ClipboardList;
  const colorStyle = { '--event-color': item.color } as CSSProperties;
  const cancelled = item.cancelled && 'calendar-event--cancelled';

  if (view === Views.MONTH) {
    return (
      <span className={clsx('calendar-event calendar-event--month', cancelled)} style={colorStyle}>
        <Icon size={11} />
        <span className="calendar-event__title">{item.title}</span>
      </span>
    );
  }

  const firstName = item.driverName?.split(' ')[0];
  return (
    <div className={clsx('calendar-event calendar-event--block', cancelled)} style={colorStyle}>
      <div className="calendar-event__head">
        <Icon size={13} />
        <span className="calendar-event__title">{item.title}</span>
      </div>
      <span className="calendar-event__time"><Clock size={10} /> {timeLabel(item)}</span>
      <span className="calendar-event__meta">
        🚛 {item.vehiclePlate ?? 'Sin vehículo'}{firstName ? ` (${firstName})` : ''}{item.kind === 'trip' ? ` · ${item.badge}` : ''}
      </span>
    </div>
  );
}

export default function CalendarPage() {
  const { user } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState<View>(() => (
    typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches ? Views.DAY : Views.WEEK
  ));
  const [filters, setFilters] = useState<CalendarFilters>({ kind: 'all', driverId: '', vehicleId: '' });
  const [detailId, setDetailId] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [defaultFrom, setDefaultFrom] = useState<Date | null>(null);

  useEffect(() => {
    document.title = 'Calendario de Programación | Paitron Radar';
  }, []);

  const isOperator = user?.role === 'operator';
  const isExecutive = user?.role === 'executive';
  const canSchedule = puedeProgramar(user?.role);

  // Se pide al servidor solo lo que el calendario está mostrando. La clave es
  // el DÍA (no el `Date`): `Hoy` crea un `Date` nuevo cada vez y sin esto cada
  // clic dispararía una carga aunque el rango no cambie.
  const viewName: CalendarViewName = view === Views.MONTH ? 'month' : view === Views.DAY ? 'day' : 'week';
  const dayMs = startOfDay(currentDate).getTime();
  const range = useMemo(() => visibleRange(viewName, new Date(dayMs)), [viewName, dayMs]);
  const query = useMemo(
    () => ({ from: range.start.toISOString(), to: range.end.toISOString() }),
    [range],
  );

  // El servidor ya acota por rol (Operador: lo propio; Supervisor: su
  // sub-unidad; Administrador y Ejecutivo: todo) — aquí no se vuelve a filtrar.
  const { trips, isLoading: tripsLoading, error: tripsError, programar } = useTrips(query);
  const { entries, isLoading: entriesLoading, error: entriesError } = useLogbookEntries(query);
  const { drivers } = useDrivers();
  const { vehicles } = useVehicles();

  const items = useMemo(
    () => [...trips.map(tripToItem), ...entries.map(logbookToItem)],
    [trips, entries],
  );
  const filtered = useMemo(() => applyFilters(items, filters), [items, filters]);

  const rbcEvents: RBCEvent[] = useMemo(
    () => filtered.map(item => ({ id: item.id, title: item.title, start: item.start, end: item.end, resource: item })),
    [filtered],
  );

  // Las opciones salen de las listas reales, no de los eventos del rango:
  // si salieran de los eventos, elegir un conductor y navegar a una semana
  // donde no tiene nada dejaría el select apuntando a una opción que ya no existe.
  const driverOptions = useMemo(
    () => [...drivers].sort((a, b) => a.name.localeCompare(b.name, 'es')),
    [drivers],
  );
  const vehicleOptions = useMemo(
    () => [...vehicles].sort((a, b) => a.plate.localeCompare(b.plate, 'es')),
    [vehicles],
  );

  const detail = detailId ? items.find(i => i.id === detailId) ?? null : null;

  const rangeLabel = useMemo(() => {
    if (view === Views.MONTH) {
      return capitalize(format(currentDate, "MMMM yyyy", { locale: es }));
    }
    if (view === Views.DAY) {
      return capitalize(format(currentDate, "EEEE d 'de' MMMM, yyyy", { locale: es }));
    }
    const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
    return `Semana del ${format(weekStart, "d 'de' MMMM, yyyy", { locale: es })}`;
  }, [view, currentDate]);

  const goToday = useCallback(() => setCurrentDate(new Date()), []);

  const goPrev = useCallback(() => setCurrentDate(d => {
    if (view === Views.MONTH) return addMonths(d, -1);
    if (view === Views.DAY) return addDays(d, -1);
    return addDays(d, -7);
  }), [view]);

  const goNext = useCallback(() => setCurrentDate(d => {
    if (view === Views.MONTH) return addMonths(d, 1);
    if (view === Views.DAY) return addDays(d, 1);
    return addDays(d, 7);
  }), [view]);

  const openScheduleForm = useCallback((from: Date | null) => {
    setDefaultFrom(from);
    setFormOpen(true);
  }, []);

  const handleSelectSlot = useCallback((slotInfo: SlotInfo) => {
    if (!canSchedule) return;
    const start = new Date(slotInfo.start);
    // En Mes el clic cae siempre a las 00:00 (no hay hora que elegir): se
    // propone la jornada, no la medianoche.
    if (viewName === 'month') start.setHours(8, 0, 0, 0);
    openScheduleForm(start);
  }, [canSchedule, viewName, openScheduleForm]);

  const handleSelectRBCEvent = useCallback((rbcEvent: RBCEvent) => {
    setDetailId(rbcEvent.resource.id);
  }, []);

  const eventPropGetter = useCallback(() => ({
    style: { backgroundColor: 'transparent', border: 'none', padding: 0, boxShadow: 'none' },
  }), []);

  const eventComponent = useCallback((props: EventProps<RBCEvent>) => (
    <CalendarEventContent {...props} view={view} />
  ), [view]);

  const isLoading = tripsLoading || entriesLoading;
  const filtersActive = filters.kind !== 'all' || filters.driverId !== '' || filters.vehicleId !== '';
  const errors = [
    tripsError && `No se pudieron cargar los viajes: ${tripsError.message}`,
    entriesError && `No se pudieron cargar las novedades: ${entriesError.message}`,
  ].filter((m): m is string => Boolean(m));

  let statusMessage: string | null = null;
  if (errors.length === 0) {
    if (isLoading) statusMessage = 'Cargando eventos…';
    else if (filtered.length === 0) {
      statusMessage = filtersActive && items.length > 0
        ? 'Ningún evento de este periodo coincide con los filtros.'
        : 'No hay viajes ni novedades en este periodo.';
    }
  }

  return (
    <div className="calendar-page">

      {/* Top Bar Header */}
      <div className="calendar-page__topbar">
        <div className="calendar-page__title-block">
          <div className="calendar-page__title-icon">
            <CalendarIcon size={20} />
          </div>
          <div>
            <h1 className="calendar-page__title">Programación y Calendario Operativo</h1>
            <p className="calendar-page__subtitle">
              {isOperator ? 'Tu agenda personal de viajes y novedades' : isExecutive ? 'Agenda de viajes y novedades de la flota' : 'Programación de viajes y novedades de la flota'}
            </p>
          </div>
        </div>

        <div className="calendar-page__controls">
          <div className="calendar-view-tabs">
            {VIEW_OPTIONS.map(opt => (
              <button
                key={opt.key}
                type="button"
                className={`calendar-view-tab${view === opt.key ? ' calendar-view-tab--active' : ''}`}
                onClick={() => setView(opt.key)}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <div className="calendar-nav">
            <button type="button" className="calendar-nav__arrow" onClick={goPrev} aria-label="Anterior">
              <ChevronLeft size={16} />
            </button>
            <button type="button" className="calendar-nav__today" onClick={goToday}>Hoy</button>
            <button type="button" className="calendar-nav__arrow" onClick={goNext} aria-label="Siguiente">
              <ChevronRight size={16} />
            </button>
          </div>

          <span className="calendar-nav__label">{rangeLabel}</span>

          <select
            value={filters.kind}
            onChange={e => setFilters(f => ({ ...f, kind: e.target.value as CalendarFilters['kind'] }))}
            className="calendar-page__filter"
            aria-label="Filtrar por tipo de evento"
          >
            {KIND_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>

          {/* Conductor y vehículo: solo tienen sentido cuando hay entre qué
              elegir. El Operador ve únicamente lo suyo (lo aplica el servidor),
              así que un selector con su propio nombre no le sirve de nada. */}
          {!isOperator && driverOptions.length > 1 && (
            <select
              value={filters.driverId}
              onChange={e => setFilters(f => ({ ...f, driverId: e.target.value }))}
              className="calendar-page__filter"
              aria-label="Filtrar por conductor"
            >
              <option value="">Todos los conductores</option>
              {driverOptions.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          )}

          {vehicleOptions.length > 1 && (
            <select
              value={filters.vehicleId}
              onChange={e => setFilters(f => ({ ...f, vehicleId: e.target.value }))}
              className="calendar-page__filter"
              aria-label="Filtrar por vehículo"
            >
              <option value="">Todos los vehículos</option>
              {vehicleOptions.map(v => <option key={v.id} value={v.id}>{v.plate}</option>)}
            </select>
          )}

          {canSchedule && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => openScheduleForm(null)}
              leftIcon={<Plus size={16} />}
            >
              Programar viaje
            </Button>
          )}
        </div>
      </div>

      {errors.map(message => (
        <p key={message} className="calendar-page__status calendar-page__status--error" role="alert">{message}</p>
      ))}
      {statusMessage && <p className="calendar-page__status" role="status">{statusMessage}</p>}

      {/* Calendar body: vistas Mes / Semana / Día */}
      <div className="calendar-page__body">
        <BigCalendar
          localizer={localizer}
          culture="es"
          events={rbcEvents}
          date={currentDate}
          view={view}
          onView={setView}
          onNavigate={setCurrentDate}
          views={[Views.MONTH, Views.WEEK, Views.DAY]}
          selectable={canSchedule}
          onSelectSlot={handleSelectSlot}
          onSelectEvent={handleSelectRBCEvent}
          eventPropGetter={eventPropGetter}
          // Default es 250ms: en touch, react-big-calendar exige mantener
          // presionado ese tiempo antes de armar la selección, y un tap
          // normal (más corto) queda cancelado por la propia librería antes
          // de llegar a onSelectSlot. Bajarlo hace que un tap común alcance.
          longPressThreshold={20}
          components={{ toolbar: () => null, event: eventComponent }}
          min={AXIS_MIN}
          max={AXIS_MAX}
          scrollToTime={AXIS_SCROLL_TO}
          popup
          style={{ height: '100%' }}
        />
      </div>

      {/* Detalle: los mismos modales de solo lectura de Viajes y Novedades.
          Sin acciones a propósito — iniciar, corregir o cancelar un viaje se
          hace en su pantalla, donde está el flujo completo. */}
      <TripDetailModal
        isOpen={detail?.kind === 'trip'}
        onClose={() => setDetailId(null)}
        trip={detail?.trip ?? null}
        role={user?.role}
      />
      <LogbookEntryDetailModal
        entry={detail?.entry ?? null}
        onClose={() => setDetailId(null)}
        puedeCorregir={false}
      />

      <TripFormModal
        isOpen={formOpen}
        onClose={() => setFormOpen(false)}
        vehicles={vehicles}
        drivers={drivers}
        onProgramar={programar}
        defaultFrom={defaultFrom}
      />
    </div>
  );
}
